/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { Box, Stack, Checkbox } from "@mui/material";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DetailRow from "../components/DetailRow";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  formatPower,
  isValidPowerSignal,
  getSignalLedColor,
  isBitSet,
  getAlarmLedColor,
} from "../utils";

const OchDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const isDisconnected = statusLabel === "Disconnected";

  // Global alarms
  const ochAlarms = parsedAttributes.ochAlarms;
  const ochAlarmsMask = parsedAttributes.ochAlarmsMask;
  const hasGlobalAlarms = ochAlarms !== undefined || ochAlarmsMask !== undefined;

  // Global alarm definitions
  const globalAlarmDefinitions: AlarmDefinition[] = [
    { bit: 4, name: "I/F Card Comm Error" },
    { bit: 5, name: "Adjustment Fault" },
  ];

  // Link-specific alarm definitions (from ochAlarmsPort)
  const linkAlarmDefinitions: AlarmDefinition[] = [
    { bit: 0, name: "Optical Adjust Fail" },
    { bit: 1, name: "UL Optical Link Failed" },
    { bit: 2, name: "Mismatch" },
  ];

  // Extract link alarm data for ports 1-4
  const linkAlarms: Array<{ linkNumber: number; linkName: string; alarms: any; mask: any }> = [];
  for (let i = 1; i <= 4; i++) {
    const alarms = parsedAttributes[`ochAlarmsPort${i}`];
    const mask = parsedAttributes[`ochAlarmsMaskPort${i}`];
    if (alarms !== undefined || mask !== undefined) {
      linkAlarms.push({
        linkNumber: i,
        linkName: `Port ${i}`,
        alarms,
        mask,
      });
    }
  }

  const hasLinkAlarms = linkAlarms.length > 0;

  // Extract power data for links
  const ochDlPower = parsedAttributes.ochDlPower;
  const ochUlPower = parsedAttributes.ochUlPower;
  const hasPowerInfo = ochDlPower !== undefined || ochUlPower !== undefined;

  // General device information
  const ochTopologyLocation = parsedAttributes.ochTopologyLocation;
  const ochEquipmentModel = parsedAttributes.ochEquipmentModel || device.model;
  const ochEquipmentSN = parsedAttributes.ochEquipmentSN || device.serialNumber;
  const hasGeneralInfo =
    ochTopologyLocation ||
    ochEquipmentModel ||
    ochEquipmentSN;

  // If alarmsOnly mode, only render alarm sections
  if (alarmsOnly) {
    if (!hasGlobalAlarms && !hasLinkAlarms) return null;
    
    return (
      <>
        {/* Global Alarms Section */}
        {hasGlobalAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarms={ochAlarms}
                alarmsMask={ochAlarmsMask}
                alarmDefinitions={globalAlarmDefinitions}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>
        )}

        {/* Link Alarms Section */}
        {hasLinkAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Link Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">Alarm</TableHead>
                    {linkAlarms.map((link) => (
                      <TableHead key={link.linkNumber} className="text-center min-w-[120px]">
                        {link.linkName}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linkAlarmDefinitions.map((alarmDef) => (
                    <TableRow key={alarmDef.bit}>
                      <TableCell>{alarmDef.name}</TableCell>
                      {linkAlarms.map((link) => {
                        const isAlarmActive = isBitSet(link.alarms, alarmDef.bit);
                        const isMaskEnabled = isBitSet(link.mask, alarmDef.bit);
                        return (
                          <TableCell key={`${link.linkNumber}-${alarmDef.bit}`} className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <div
                                className="w-2.5 h-2.5 rounded-full"
                                style={{ backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive) }}
                              />
                              <Checkbox
                                checked={isMaskEnabled}
                                disabled
                                size="small"
                                sx={{
                                  padding: 0,
                                  color: "var(--foreground)",
                                  "&.Mui-disabled": { color: "var(--muted-foreground)" },
                                }}
                              />
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </>
    );
  }

  if (!hasGeneralInfo && !hasPowerInfo) return null;

  return (
    <>
      {hasGeneralInfo && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Device Information</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {ochTopologyLocation && <DetailRow label="Topology Location" value={ochTopologyLocation} />}
            {ochEquipmentModel && <DetailRow label="Model" value={ochEquipmentModel} />}
            {ochEquipmentSN && <DetailRow label="Serial Number" value={ochEquipmentSN} />}
          </CardContent>
        </Card>
      )}

      {/* Power Information Section */}
      {hasPowerInfo && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">RF Information</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {ochDlPower !== undefined && (
              <div className="flex gap-2 mb-2">
                <span className="text-sm text-muted-foreground font-medium w-2/5 shrink-0">DL Power</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getSignalLedColor(isValidPowerSignal(ochDlPower)) }} />
                  <span className="text-sm text-foreground">{formatPower(ochDlPower)}</span>
                </div>
              </div>
            )}
            {ochUlPower !== undefined && (
              <div className="flex gap-2 mb-2">
                <span className="text-sm text-muted-foreground font-medium w-2/5 shrink-0">UL Power</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getSignalLedColor(isValidPowerSignal(ochUlPower)) }} />
                  <span className="text-sm text-foreground">{formatPower(ochUlPower)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

    </>
  );
};


export default OchDeviceDetails;
